// "Spaces in <Circle>" on the "+" page (07-frame.md, The rail, 6): the circle's "Everyone in <Circle>"
// spaces the person hasn't joined, from the chat server's room directory (spaces only), each with
// Join. "+" carries a dot while one of them is new to the person (not yet seen on that page; remembered
// per device and account, v1.58.17). The directory is read at the start, whenever "+" opens, and on
// window focus when the last read is older than 5 minutes (v1.58.17); never polled.
import { EventType, JoinRule, Method, RoomType, Visibility, type IPublicRoomsChunkRoom, type MatrixClient, type MatrixError, type Room } from 'matrix-js-sdk';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { circleName } from '../accounts/accounts';
import { Avatar } from '../blocks/Bits';
import { Button } from '../blocks/Button';
import { toast } from '../blocks/Toast';
import { describe } from '../core/errors';
import { navigate } from '../core/router';
import { readJson, writeJson } from '../core/storage';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { thumbnail } from '../session/media';
import { isJoined } from '../session/rooms';
import { liveSync, whenLive } from '../session/client';
import { mainSpace } from './Banner';

const STALE_MS = 5 * 60_000;
let found: IPublicRoomsChunkRoom[] = [];
let lastRead = 0;
/** The spaces already shown on the "+" page, per account (v1.58.17). */
const seenKey = (client: MatrixClient) => `desyr_plus_seen:${client.getUserId()}`;
export const seenOf = (client: MatrixClient) => new Set(readJson<string[]>(seenKey(client)) ?? []);
const listeners = new Set<() => void>();
let version = 0;
const changed = () => (version++, listeners.forEach((l) => l()));
const useVersion = () => useSyncExternalStore((l) => (listeners.add(l), () => void listeners.delete(l)), () => version);
let read: MatrixClient | undefined;

/** Reads the directory's spaces again (at most 10 pages); a failure leaves the list and the dot as they were. */
async function readDirectory(client: MatrixClient) {
  const rooms: IPublicRoomsChunkRoom[] = [];
  let since: string | undefined;
  for (let page = 0; page < 10; page++) {
    const answer = await client.publicRooms({ filter: { room_types: [RoomType.Space] }, limit: 100, since });
    rooms.push(...answer.chunk);
    if (!(since = answer.next_batch)) break;
  }
  found = rooms;
  lastRead = Date.now();
  changed();
}

const listing = new Map<string, number>();
/**
 * Listing it later (09, v1.58.21): a moderator opening an "Everyone in <Circle>" space the directory
 * doesn't list publishes it once, silently; a failure is tried at most once more in this session.
 */
export async function listOnce(client: MatrixClient, space: Room): Promise<void> {
  await liveSync(client); // the space's power levels from the server, not this device's copy
  const create = space.currentState.getStateEvents(EventType.RoomCreate, '')?.getContent();
  const tries = listing.get(space.roomId) ?? 0;
  if (tries >= 2 || create?.['m.federate'] !== false || space.getJoinRule() !== JoinRule.Public) return;
  const power = space.currentState.getStateEvents(EventType.RoomPowerLevels, '')?.getContent();
  if ((power?.users?.[client.getUserId()!] ?? power?.users_default ?? 0) < 50) return;
  listing.set(space.roomId, tries + 1);
  try {
    if ((await client.getRoomDirectoryVisibility(space.roomId)).visibility !== Visibility.Private) return void listing.set(space.roomId, 2);
    await client.setRoomDirectoryVisibility(space.roomId, Visibility.Public);
    listing.set(space.roomId, 2);
  } catch {
    // never shown; the next opening tries once more
  }
}

const MAIN = 'chat.desyr.main_space';
/**
 * The space to join (17, v1.58.47): of the circle's own directory, the public spaces whose room ID is on
 * the circle's server, or has no server part (room version 12, v1.58.52); the most joined members, equal
 * counts going to the lower room ID (v1.58.46). Whether it is federated isn't seen before joining: that
 * is the circle's set-up's.
 */
export const mainCandidate = (rooms: IPublicRoomsChunkRoom[], server: string) =>
  rooms
    .filter((r) => r.room_type === RoomType.Space && r.join_rule === JoinRule.Public && (!r.room_id.includes(':') || serverOf(r.room_id) === server))
    .sort((x, y) => y.num_joined_members - x.num_joined_members || (x.room_id < y.room_id ? -1 : 1))[0];

/**
 * Joining it once (17-people.md, v1.58.42): after the first sync (under the start screen's fade at most), one
 * who has joined no "Everyone in <Circle>" space joins, silently, the main candidate. Only a join that
 * worked is recorded (`chat.desyr.main_space`); with the record nothing is joined again (a later
 * Leave stays). A failure is tried again at the next open.
 */
export async function joinMainSpace(client: MatrixClient): Promise<void> {
  await whenLive(client);
  if (mainSpace(client)) return;
  try {
    // The record asked of the server itself: a first sync can bring an older one, which this device keeps.
    const path = `/user/${encodeURIComponent(client.getUserId()!)}/account_data/${MAIN}`;
    const record = await client.http.authedRequest<{ joined?: string }>(Method.Get, path).catch((e: MatrixError) => (e.httpStatus === 404 ? { joined: undefined } : Promise.reject(e)));
    if (record.joined) return;
    await readDirectory(client);
    const space = mainCandidate(found, serverOf(client.getUserId()!));
    if (!space) return;
    await client.joinRoom(space.room_id);
    await client.setAccountData(MAIN as never, { joined: space.room_id } as never);
  } catch {
    // never shown; the next open tries again
  }
}

/** A failed join in 02's words for joining, with "space" in place of "channel" (v1.58.17). */
export const joinWords = (error: unknown) =>
  describe(error, 'join', (kind) => (kind === 'IV' ? t('error.joinSpaceInviteOnly') : ['NW', 'SV', 'RL', 'SO', 'XX'].includes(kind) ? undefined : t('error.joinSpaceOther'))).text;

/** The listed spaces the person isn't in. */
export const notJoined = (client: MatrixClient, rooms: IPublicRoomsChunkRoom[]) => rooms.filter((r) => r.room_type === RoomType.Space && !isJoined(client.getRoom(r.room_id)));

/** Whether "+" shows its dot: read at the start, and again on focus once the last read is stale. */
export function useNewCircleSpace(client: MatrixClient): boolean {
  useVersion();
  useEffect(() => {
    if (read === client) return;
    void readDirectory((read = client)).catch(() => {});
    addEventListener('focus', () => Date.now() - lastRead > STALE_MS && void readDirectory(client).catch(() => {}));
  }, [client]);
  const seen = seenOf(client);
  return notJoined(client, found).some((r) => !seen.has(r.room_id));
}

export function CircleSpaces({ client }: { client: MatrixClient }) {
  useVersion();
  const [joining, setJoining] = useState<string>();
  const [joined, setJoined] = useState<string>();
  const shown = notJoined(client, found);
  useEffect(() => void readDirectory(client).catch(() => {}), [client]);
  // What this page shows counts as seen; "+" loses its dot. Only spaces still listed are kept.
  useEffect(() => {
    const seen = seenOf(client);
    if (shown.every((r) => seen.has(r.room_id))) return;
    writeJson(seenKey(client), found.map((r) => r.room_id).filter((id) => seen.has(id) || shown.some((r) => r.room_id === id)));
    changed();
  });
  // Once the joined space is in the lists it opens (and is on the rail).
  useEffect(() => {
    if (joined && isJoined(client.getRoom(joined))) navigate(`/${encodeURIComponent(joined)}/`);
  });
  const join = async (roomId: string) => {
    setJoining(roomId);
    try {
      setJoined((await client.joinRoom(roomId)).roomId);
    } catch (e) {
      toast(t('frame.cantJoin', { reason: joinWords(e) })); // the row stays
    } finally {
      setJoining(undefined);
    }
  };
  if (!shown.length) return null;
  return (
    <section className="circle-spaces" aria-labelledby="circle-spaces">
      <h3 id="circle-spaces" className="form-section">{t('frame.circleSpaces', { circle: circleName(client.getUserId()!) })}</h3>
      {shown.map((r) => (
        <div key={r.room_id} className="lobby-card">
          <Avatar id={r.room_id} name={r.name ?? ''} src={thumbnail(client, r.avatar_url, 42)} size={42} round={false} letters={2} />
          <div className="lobby-card-text">
            <strong>{r.name}</strong>
            <span className="lobby-card-line">
              {t('lobby.members', { n: r.num_joined_members })}
              {r.topic && <span className="lobby-card-topic">{r.topic}</span>}
            </span>
          </div>
          <Button small busy={joining === r.room_id} disabled={!!joining} onClick={() => void join(r.room_id)}>
            {joining === r.room_id ? t('frame.joining') : t('lobby.join')}
          </Button>
        </div>
      ))}
    </section>
  );
}
